import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { DialogPortal } from '../ui/dialogHost';
import { api, ApiError, fieldIssues } from '../api';
import { warsawInstant, warsawParts } from '../events/labels';
import { ConflictNotice } from '../ui/ConflictNotice';
import { trapTab } from '../ui/focus';
import type { Task } from './types';
import { DialogLayer } from '../ui/Dialog';
import { leaveGuarded, sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';

/**
 * "Przenieś termin": a new due day and time with a reason (the server refuses a new due time without one).
 * Moved to later, the task is no longer overdue and its reminders stop.
 */
export function RescheduleDialog({ task, onClose, onDone }: { task: Task; onClose: () => void; onDone: () => Promise<unknown> | void }) {
  const start = task.dueAt ? warsawParts(task.dueAt) : { day: '', time: '09:00' };
  const [day, setDay] = useState(start.day);
  const [time, setTime] = useState(start.time);
  const [reason, setReason] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('input')?.focus();
  }, []);
  const save = useMutation({
    mutationFn: () => api<Task>(`/api/schedule/tasks/${task.id}`, { method: 'PATCH', body: { version: task.version, dueAt: warsawInstant(day, time), reason: reason.trim() } }),
    onSuccess: async () => {
      await onDone();
      onClose();
    },
  });
  const ready = day !== '' && time !== '' && reason.trim() !== '';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) save.mutate();
  };
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const now = { day, time, reason };
  const opening = useOpening(now);
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(now, opening),
    save: async () => {
      if (!ready) return false;
      await save.mutateAsync();
      return true;
    },
  });
  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <DialogPortal>
      <div className="dialog-backdrop" onPointerDown={(e) => e.target === e.currentTarget && leaveGuarded(onClose, e.currentTarget)}>
        <div
          ref={ref}
          className="dialog panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reschedule-title"
          data-feature-id="schedule.reschedule"
          onKeyDown={(e) => {
            if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
          }}
        >
          <DialogLayer onClose={onClose} busy={save.isPending} />
          <h2 id="reschedule-title">Przenieś termin</h2>
          <p className="muted">{task.title}</p>
          <form ref={guardRef} onSubmit={submit} aria-label="Nowy termin zadania">
            <div className="field-row">
              <label className="field">
                <span>Dzień</span>
                <input type="date" value={day} onChange={(e) => setDay(e.target.value)} required />
              </label>
              <label className="field">
                <span>Godzina</span>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} required />
              </label>
            </div>
            <label className="field">
              <span>Powód przeniesienia</span>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="np. sala otwarta dopiero w piątek" maxLength={1000} autoComplete="off" required />
            </label>
            {error?.status === 409 && <ConflictNotice onReload={() => onClose()} />}
            {error && error.status !== 409 && (
              <p className="alert" role="alert">
                {fieldIssues(error).map((i) => i.message).join(' ') || error.message}
              </p>
            )}
            <div className="actions">
              <button className="btn primary" type="submit" disabled={!ready || save.isPending}>
                {save.isPending ? 'Zapisywanie…' : 'Przenieś'}
              </button>
              <button className="btn" type="button" onClick={onClose}>
                Anuluj
              </button>
            </div>
          </form>
        </div>
      </div>
    </DialogPortal>
  );
}
